import { useCallback, useEffect, useState } from 'react'
import type { Diamond } from '../api/types'

/**
 * Representative-image fallback cache.
 *
 * Supplier-hosted imageUrls 404 constantly. Rather than drop straight to
 * an abstract silhouette, we substitute a REAL catalog photo from another
 * stone of the same cut (and, when possible, the same colour family) — a
 * representative image. Buyers see an actual diamond of the right shape;
 * a "Representative" hint keeps them from thinking it's the exact stone.
 *
 * Two module-level maps, keyed by `${shape}|${family}` and `${shape}`:
 *   - `verified`  — URLs we've watched load (img onLoad). Trustworthy.
 *   - `seeded`    — candidate URLs pulled from a result set but not yet
 *                   confirmed. Used only when no verified image exists.
 * A representative that itself 404s advances to the silhouette, so a bad
 * seed just degrades gracefully.
 */
const verified = new Map<string, string>()
const seeded = new Map<string, string>()

/** "Fancy Vivid Blue" → "Blue"; single grades D..M (any letter) → "White". */
export function colorFamily(color: string | null | undefined): string {
  const c = (color || '').trim()
  if (!c) return 'White'
  if (/^[A-Za-z]$/.test(c)) return 'White'
  const parts = c.split(/\s+/).filter(Boolean)
  const hue = parts[parts.length - 1]
  return /^[A-Za-z]+$/.test(hue)
    ? hue.charAt(0).toUpperCase() + hue.slice(1).toLowerCase()
    : 'White'
}

function keysFor(shape: string | null | undefined, color: string | null | undefined): [string, string] {
  const sh = (shape || '').trim().toLowerCase() || 'unknown'
  return [`${sh}|${colorFamily(color).toLowerCase()}`, sh]
}

/** Record a URL we've seen successfully load. First-write-wins per key. */
export function registerImage(shape: string | null | undefined, color: string | null | undefined, url: string | null | undefined) {
  if (!url) return
  const [sc, s] = keysFor(shape, color)
  if (!verified.has(sc)) verified.set(sc, url)
  if (!verified.has(s)) verified.set(s, url)
}

/** Optimistically seed candidates from a result set so early failures still
 *  have a real photo to reach for. Never overrides verified entries. */
export function seedRepresentatives(stones: Diamond[]) {
  for (const st of stones) {
    const url = st.media?.imageUrl
    if (!url) continue
    const [sc, s] = keysFor(st.shape, st.color)
    if (!seeded.has(sc)) seeded.set(sc, url)
    if (!seeded.has(s)) seeded.set(s, url)
  }
}

/**
 * A representative URL just 404'd — evict it from BOTH maps so no other
 * stone reaches for the same dead link. Seeded URLs are unproven (they can
 * be as dead as the owning stone's own image), so a whole supplier batch of
 * dead photos used to seed one dead URL per shape and then every substitute
 * that grabbed it fell straight to the bundled studio render. Purging lets
 * the NEXT candidate (often a verified, proven-loading photo from a healthy
 * supplier) take over, so a real same-shape catalog photo shows instead.
 */
export function purgeRepresentative(url: string | null | undefined) {
  if (!url) return
  for (const [k, v] of verified) if (v === url) verified.delete(k)
  for (const [k, v] of seeded) if (v === url) seeded.delete(k)
}

/** True for the bundled, can't-404 local per-shape photo (the terminal floor)
 *  — as opposed to a real catalog photo borrowed from another stone. */
export function isBundledPhoto(url: string | null | undefined): boolean {
  return !!url && url.startsWith('/diamonds/')
}

/**
 * Baked-in, LOCAL per-shape diamond photos — the guaranteed terminal
 * fallback. Bundled under web/public/diamonds/<shape>.webp (real
 * photorealistic lab-grown diamond renders on a navy→cream studio
 * gradient), so a real stone photo ALWAYS shows and can never 404.
 *
 * HARD REQUIREMENT (Tej, rehaul 2026-07-08): "nowhere on DiaEx should we
 * see silhouettes, not even home page." The old abstract StoneSilhouette
 * SVG is dead — this map is the last link in the fallback chain and it is
 * a raster photo, so the chain never degrades to a wireframe.
 *
 * Every shape resolves: exact key → normalized family → Round. Refresh
 * via scripts/qa/harvest-rep-defaults or regenerate the /diamonds assets.
 */
const SHAPE_PHOTOS: Record<string, string> = {
  round: '/diamonds/round.webp',
  oval: '/diamonds/oval.webp',
  emerald: '/diamonds/emerald.webp',
  pear: '/diamonds/pear.webp',
  cushion: '/diamonds/cushion.webp',
  radiant: '/diamonds/radiant.webp',
  princess: '/diamonds/princess.webp',
  marquise: '/diamonds/marquise.webp',
  heart: '/diamonds/heart.webp',
  asscher: '/diamonds/asscher.webp',
}

/** Map any free-text shape onto a bundled photo. Prefix-matches the same
 *  vocabulary the silhouette used, so aliases (square→princess,
 *  octagon→emerald, trillion/baguette→round) still land on a real photo. */
export function shapePhoto(shape: string | null | undefined): string {
  const s = (shape || '').trim().toLowerCase()
  if (SHAPE_PHOTOS[s]) return SHAPE_PHOTOS[s]
  if (s.startsWith('rou')) return SHAPE_PHOTOS.round
  if (s.startsWith('ova')) return SHAPE_PHOTOS.oval
  if (s.startsWith('eme') || s.startsWith('oct')) return SHAPE_PHOTOS.emerald
  if (s.startsWith('pea')) return SHAPE_PHOTOS.pear
  if (s.startsWith('cus')) return SHAPE_PHOTOS.cushion
  if (s.startsWith('rad')) return SHAPE_PHOTOS.radiant
  if (s.startsWith('pri') || s.startsWith('squ')) return SHAPE_PHOTOS.princess
  if (s.startsWith('mar') || s.startsWith('nav')) return SHAPE_PHOTOS.marquise
  if (s.startsWith('hea')) return SHAPE_PHOTOS.heart
  if (s.startsWith('asc') || s.startsWith('ass')) return SHAPE_PHOTOS.asscher
  return SHAPE_PHOTOS.round
}

/** Best representative URL for a shape (+colour family), never `exclude`.
 *  Prefers shape+family, then shape-only; verified before seeded; finally
 *  the bundled local per-shape photo (Round as the ultimate fallback).
 *  ALWAYS returns a string — a real photo is guaranteed, never a silhouette. */
export function getRepresentative(
  shape: string | null | undefined,
  color: string | null | undefined,
  exclude?: string | null,
): string {
  const [sc, s] = keysFor(shape, color)
  for (const m of [verified, seeded]) {
    const a = m.get(sc); if (a && a !== exclude) return a
    const b = m.get(s); if (b && b !== exclude) return b
  }
  const d = shapePhoto(shape)
  // The bundled photo can't 404; if it somehow equals `exclude`, fall to Round.
  return d !== exclude ? d : SHAPE_PHOTOS.round
}

// 'own' = the stone's own imageUrl; 'representative' = a same-shape catalog
// photo; 'photo' = the bundled local per-shape diamond photo (terminal).
// There is intentionally NO silhouette stage — the chain always ends on a
// real raster image.
type Stage = 'own' | 'representative' | 'photo'
interface ImgState { src: string; stage: Stage }

function initialState(ownUrl: string | null, shape: string | null | undefined, color: string | null | undefined): ImgState {
  if (ownUrl) return { src: ownUrl, stage: 'own' }
  return { src: getRepresentative(shape, color), stage: 'representative' }
}

/**
 * Drives the fallback chain for one stone's image. Returns the src to
 * render (null ⇒ show the silhouette), whether it's a substitute (for the
 * hint), and load/error handlers to wire onto the <img>.
 */
export function useStoneImage(stone: Pick<Diamond, 'shape' | 'color' | 'media'> | null) {
  const ownUrl = stone?.media?.imageUrl ?? null
  const shape = stone?.shape ?? null
  const color = stone?.color ?? null

  const [state, setState] = useState<ImgState>(() => initialState(ownUrl, shape, color))

  useEffect(() => {
    setState(initialState(ownUrl, shape, color))
  }, [ownUrl, shape, color])

  const onLoad = useCallback(() => {
    // Only the stone's OWN image proves a URL good for its shape/family.
    setState((s) => {
      if (s.stage === 'own' && s.src) registerImage(shape, color, s.src)
      return s
    })
  }, [shape, color])

  const onError = useCallback(() => {
    setState((s) => {
      if (s.stage === 'own') {
        // Own image 404'd — borrow a REAL same-shape catalog photo. If the
        // best candidate is already the bundled floor, mark it 'photo'.
        const next = getRepresentative(shape, color, s.src)
        return { src: next, stage: isBundledPhoto(next) ? 'photo' : 'representative' }
      }
      if (s.stage === 'representative') {
        // A borrowed photo 404'd too (seeds are unproven). Evict it so nobody
        // reuses the dead link, then reach for the NEXT real same-shape photo
        // — only drop to the bundled studio render when no real photo is left.
        purgeRepresentative(s.src)
        const next = getRepresentative(shape, color, s.src)
        return { src: next, stage: isBundledPhoto(next) ? 'photo' : 'representative' }
      }
      // Already on the bundled photo; nothing lower to fall to.
      return s
    })
  }, [shape, color])

  return {
    // Always a real image src — never null. `isSubstitute` drives the
    // "Representative" hint (own image wasn't available).
    src: state.src,
    isSubstitute: state.stage !== 'own',
    onLoad,
    onError,
  }
}
